import { getContrast } from "color2k";
import {
  getTextColorForBackground,
  OPTIMAL_TEXT_COLOR_WHITE,
} from "@certn/cascadia-react/utils/colorValidation";

// Re-export for use in tests and other components
export { getTextColorForBackground };

// Optimal text colors matching backend (only two options)

/**
 * Calculates the contrast ratio between a background color and its optimal text color
 * @param backgroundColor - The background color in hex format
 * @returns The contrast ratio as a number (e.g., 4.5)
 */
export function getContrastRatio(backgroundColor: string): number {
  const textColor = getTextColorForBackground(backgroundColor);
  try {
    return getContrast(backgroundColor, textColor);
  } catch {
    return 1; // Return minimum contrast if calculation fails
  }
}

/**
 * Formats the contrast ratio for display
 * @param ratio - The contrast ratio as a number
 * @returns Formatted string with accessibility rating
 */
export function formatContrastRatio(ratio: number): string {
  const formatted = ratio.toFixed(1);
  if (ratio >= 7) {
    return `${formatted}:1 (AAA)`;
  } else if (ratio >= 4.5) {
    return `${formatted}:1 (AA)`;
  } else {
    return `${formatted}:1 (Fail)`;
  }
}

/**
 * Generates a WCAG compliance message based on the contrast ratio
 * @param backgroundColor - The background color in hex format
 * @returns A descriptive message about WCAG compliance
 */
export function generateWCAGMessage(backgroundColor: string): string {
  const ratio = getContrastRatio(backgroundColor);
  const textColor = getTextColorForBackground(backgroundColor);
  const textColorName =
    textColor === OPTIMAL_TEXT_COLOR_WHITE ? "white" : "black";

  if (ratio >= 7) {
    return `WCAG 2.2 AAA color contrast achieved with a ratio of ${ratio.toFixed(1)}:1 using ${textColorName} text on this background color.`;
  } else if (ratio >= 4.5) {
    return `WCAG 2.2 AA color contrast achieved with a ratio of ${ratio.toFixed(1)}:1 using ${textColorName} text on this background color.`;
  } else {
    return `WCAG 2.2 color contrast requirement not met. Current ratio is ${ratio.toFixed(1)}:1 with ${textColorName} text, but a minimum of 4.5:1 is required for normal text accessibility.`;
  }
}

/**
 * Checks if a color meets WCAG compliance standards
 * @param backgroundColor - The background color in hex format
 * @returns True if the color meets AA or AAA standards (ratio >= 4.5:1)
 */
export function isWCAGCompliant(backgroundColor: string): boolean {
  const ratio = getContrastRatio(backgroundColor);
  return ratio >= 4.5;
}
